import { ChangeDetectorRef, Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ModalController, IonicModule } from '@ionic/angular';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ResourceService } from 'src/app/core/services/resource.service';
import { ImageCompressionService } from 'src/app/core/services/image-compression.service';
import { ResourcesEndpointEnum } from 'src/app/core/enums/resources-endpoint.enum';
import { ToastrService } from 'ngx-toastr';
import { CollapsableTitleInterface, EmbedConfigModel } from 'src/app/core/interfaces/Collapsable-title';
import { GroupsStore } from 'src/app/core/stores/groups.store';
import { CategoriesStore } from 'src/app/core/stores/categories.store';
import { ReportsStore } from 'src/app/core/stores/reports.store';

@Component({
    selector: 'app-collapsable-title-modal',
    templateUrl: './collapsable-title-modal.component.html',
    styleUrls: ['./collapsable-title-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    IonicModule,
    ReactiveFormsModule,
    TranslateModule
]
})
export class CollapsableTitleModalComponent implements OnInit {
  @Input() collapsableTitleData?: CollapsableTitleInterface;
  @Input() mode: 'create' | 'edit' = 'edit';
  @Input() showEmbedConfig: boolean = true;
  @Input() showBasicFields: boolean = true;
  @Input() portalVersion: 'V1' | 'V2' = 'V1';

  iconOptions: string[] = [
    'analytics-outline',
    'bar-chart-outline',
    'build-outline',
    'business-outline',
    'cloud-outline',
    'color-filter-outline',
    'construct-outline',
    'images-outline',
    'game-controller-outline',
    'document-text-outline',
    'file-tray-stacked-outline',
    'folder-open-outline',
    'heart-outline',
    'home-outline',
    'laptop-outline',
    'people-circle-outline',
    'people-outline',
    'person-outline',
    'podium-outline',
    'school-outline',
    'settings-outline',
  ];

  renameForm!: FormGroup;
  isProcessing = false;
  isSubmitted = false;
  embedImageResponses: Map<number, any> = new Map();
  private categoriesStore = inject(CategoriesStore);

  constructor(
    private modalController: ModalController,
    private formBuilder: FormBuilder,
    private resourceService: ResourceService,
    private imageCompressionService: ImageCompressionService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private groupsStore: GroupsStore,
    private reportsStore: ReportsStore
  ) {}

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  ngOnInit() {
    this.renameForm = this.formBuilder.group({
      newTitle: [this.collapsableTitleData?.Title ?? '', [Validators.required, Validators.minLength(1), Validators.maxLength(36)]],
      published: [this.collapsableTitleData?.Published ?? true],
      description: [this.collapsableTitleData?.Description ?? '', [Validators.maxLength(70)]],
      subTitles: this.formBuilder.array(
        (this.collapsableTitleData?.SubTitles ?? []).map(s => this.formBuilder.control(s, [Validators.required, Validators.maxLength(28)]))
      ),
      embedConfig: this.formBuilder.array(
        (this.collapsableTitleData?.EmbedConfig ?? []).map(e => this.createEmbedConfigGroup(e))
      ),
      logoColor: [this.collapsableTitleData?.LogoColor ?? '#000000'],
      sectionLogo: [this.collapsableTitleData?.SectionLogo ?? '']
    });
  }

  get subTitlesArray(): FormArray {
    return this.renameForm.get('subTitles') as FormArray;
  }

  get embedConfigArray(): FormArray {
    return this.renameForm.get('embedConfig') as FormArray;
  }

  createEmbedConfigGroup(item?: EmbedConfigModel): FormGroup {
    return this.formBuilder.group({
      Id: [item?.Id ?? ''],
      Label: [item?.Label ?? '', Validators.required],
      Url: [item?.Url ?? '', Validators.required],
      EmbedOption: [item?.EmbedOption ?? 'newpage', Validators.required],
      ImageURI: [item?.ImageURI ?? ''],
      Pinned: [item?.Pinned ?? false]
    });
  }

  addSubTitle() {
    if (this.subTitlesArray.length >= 3) return;
    this.subTitlesArray.push(this.formBuilder.control('', [Validators.required, Validators.maxLength(28)]));
  }

  removeSubTitle(index: number) {
    this.subTitlesArray.removeAt(index);
  }

  addEmbedConfig() {
    this.embedConfigArray.push(this.createEmbedConfigGroup());
  }

  removeEmbedConfig(index: number) {
    this.embedConfigArray.removeAt(index);
    this.embedImageResponses.delete(index);
  }

  uploadEmbedImage(event: Event, index: number) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (file.type.includes('svg')) {
      this.translateService.get('Logo File Format should be png, jpeg, jpg').subscribe(t => this.toastrService.error(t));
      return;
    }
    const collapsableTitleID = this.collapsableTitleData?.CollapsableTitleID;
    const embedConfigId = this.embedConfigArray.at(index).get('Id')?.value;
    this.imageCompressionService.compress(file, { maxWidth: 800, maxHeight: 800, quality: 0.8 }).then(compressedFile => {
      this.resourceService.post<any>(
        ResourcesEndpointEnum.UPLOAD_EMBED_CONFIG_IMAGE,
        compressedFile,
        { collapsableTitleID, embedConfigId, fileName: compressedFile.name }
      ).subscribe({
        next: (response: any) => {
          this.embedImageResponses.set(index, response);
          this.embedConfigArray.at(index).get('ImageURI')?.setValue(response?.ImageURI ?? response?.imageURI ?? '');
          this.cdr.detectChanges();
          this.translateService.get('Image uploaded successfully').subscribe(t => this.toastrService.success(t));
        },
        error: () => {
          this.translateService.get('Error uploading image').subscribe(t => this.toastrService.error(t));
        }
      });
    });
  }

  dismiss() {
    this.modalController.dismiss(null, 'cancel');
  }

  submit() {
    this.isSubmitted = true;
    if (this.renameForm.invalid) return;

    const { newTitle, published, description, subTitles, embedConfig, logoColor, sectionLogo } = this.renameForm.getRawValue();

    // Spread original data as base so no fields are ever lost, then override with current form values
    const payload: CollapsableTitleInterface = {
      ...this.collapsableTitleData,
      CollapsableTitleID: this.collapsableTitleData?.CollapsableTitleID ?? 0,
      Title: (newTitle as string).trim(),
      Published: published,
      Description: description ?? '',
      SubTitles: (subTitles as string[]).map(s => s.trim()).filter(s => s),
      EmbedConfig: (embedConfig as EmbedConfigModel[]).map(({ Id, ...rest }) => Id ? { Id, ...rest } : rest) as EmbedConfigModel[],
      SectionLogo: (sectionLogo as string)?.trim() || undefined,
      LogoColor: logoColor ?? this.collapsableTitleData?.LogoColor
    };

    this.isProcessing = true;
    if (this.isEditMode) {
      // console.log('Editing title with payload:', payload);
      // const { Title, ...payloadWithoutTitle } = payload;
      this.resourceService.put<CollapsableTitleInterface>(
        ResourcesEndpointEnum.PUT_COLLAPSABLE_TITLE_GROUPS, payload,
        { collapsableTitleID: payload.CollapsableTitleID }
      ).subscribe({
        next: async (updatedTitle) => {
          this.groupsStore.myGroups = null;
          this.groupsStore.groups = null;
          this.categoriesStore.categories = null;
          this.reportsStore.pinnedFavorites = null;
          const result: CollapsableTitleInterface = (updatedTitle as CollapsableTitleInterface) ?? payload;
          this.translateService.get(['Saved successfully', 'Success']).subscribe(t => {
            this.toastrService.success(t['Saved successfully'], t['Success']);
          });
          this.modalController.dismiss({ oldTitle: this.collapsableTitleData?.Title, newTitleData: result }, 'renamed');
          this.cdr.detectChanges();
        },
        error: () => {
          this.translateService.get(['Error saving changes', 'Error']).subscribe(t => {
            this.toastrService.error(t['Error saving changes'], t['Error']);
          });
          this.isProcessing = false;
          this.cdr.detectChanges();
        }
      });
    } else {
      this.resourceService.post<CollapsableTitleInterface>(
        ResourcesEndpointEnum.POST_COLLAPSABLE_TITLE,
        { title: payload.Title, ...payload }
      ).subscribe({
        next: (createdTitle) => {
          const result: CollapsableTitleInterface = (createdTitle as CollapsableTitleInterface) ?? payload;
          this.categoriesStore.categories = null;
          this.translateService.get(['Created successfully', 'Success']).subscribe(t => {
            this.toastrService.success(t['Created successfully'], t['Success']);
          });
          this.modalController.dismiss({ newTitleData: result }, 'created');
          this.cdr.detectChanges();
        },
        error: () => {
          this.translateService.get(['Error creating', 'Error']).subscribe(t => {
            this.toastrService.error(t['Error creating'], t['Error']);
          });
          this.isProcessing = false;
          this.cdr.detectChanges();
        }
      });
    }
  }
}
